export interface ParsedCondition {
  column: string;
  operator: string;
  value: string;
}

export interface ParsedSort {
  column: string;
  ascending: boolean;
}

export interface ParsedBuilderQuery {
  table: string;
  fields: string[];
  conditions: ParsedCondition[];
  sorts: ParsedSort[];
  limit?: number;
}

const COMPARISONS: Record<string, string> = {
  '=': 'eq',
  '!=': 'neq',
  '<>': 'neq',
  '>': 'gt',
  '>=': 'gte',
  '<': 'lt',
  '<=': 'lte',
};

/**
 * Read a single SELECT that the visual builder can represent.
 * Returns null for anything richer: joins, OR, functions, aliases, placeholders.
 */
export function parseBuilderSql(source: string): ParsedBuilderQuery | null {
  const sql = source.trim().replace(/;\s*$/, '');
  if (!sql) return null;
  let index = 0;

  const skip = () => {
    while (index < sql.length && /\s/.test(sql[index])) index += 1;
  };

  const peekWord = (): string | null => {
    skip();
    const match = /^[A-Za-z_][A-Za-z0-9_]*/.exec(sql.slice(index));
    return match ? match[0] : null;
  };

  const consumeWord = (expected?: string): string | null => {
    const word = peekWord();
    if (!word) return null;
    if (expected && word.toLowerCase() !== expected.toLowerCase()) return null;
    index += word.length;
    return word;
  };

  const identifier = (): string | null => {
    skip();
    if (sql[index] === '"') {
      let value = '';
      index += 1;
      while (index < sql.length) {
        if (sql[index] === '"') {
          if (sql[index + 1] === '"') {
            value += '"';
            index += 2;
            continue;
          }
          index += 1;
          return value;
        }
        value += sql[index];
        index += 1;
      }
      return null;
    }
    const word = peekWord();
    if (!word) return null;
    const lower = word.toLowerCase();
    if (['select', 'from', 'where', 'and', 'or', 'order', 'by', 'limit', 'asc', 'desc', 'is', 'in', 'not', 'like', 'ilike', 'true', 'false', 'null', 'as', 'join'].includes(lower)) {
      return null;
    }
    index += word.length;
    return lower;
  };

  const literal = (): string | null => {
    skip();
    if (sql[index] === "'") {
      let value = '';
      index += 1;
      while (index < sql.length) {
        if (sql[index] === "'") {
          if (sql[index + 1] === "'") {
            value += "'";
            index += 2;
            continue;
          }
          index += 1;
          return value;
        }
        value += sql[index];
        index += 1;
      }
      return null;
    }
    const number = /^-?\d+(?:\.\d+)?/.exec(sql.slice(index));
    if (number && (sql[index] !== '-' || /\d/.test(sql[index + 1] ?? ''))) {
      index += number[0].length;
      return number[0];
    }
    const word = peekWord();
    if (word && ['true', 'false', 'null'].includes(word.toLowerCase())) {
      index += word.length;
      return word.toLowerCase();
    }
    return null;
  };

  const comparison = (): string | null => {
    skip();
    const two = sql.slice(index, index + 2);
    if (two === '!=' || two === '<>' || two === '>=' || two === '<=') {
      index += 2;
      return two;
    }
    const one = sql[index];
    if (one === '=' || one === '>' || one === '<') {
      index += 1;
      return one;
    }
    return null;
  };

  const condition = (): ParsedCondition | null => {
    const column = identifier();
    if (!column) return null;
    const word = peekWord()?.toLowerCase();
    if (word === 'is') {
      consumeWord('is');
      if (peekWord()?.toLowerCase() === 'not') return null;
      const value = consumeWord();
      if (!value || !['true', 'false', 'null'].includes(value.toLowerCase())) return null;
      return { column, operator: 'is', value: value.toLowerCase() };
    }
    if (word === 'in') {
      consumeWord('in');
      skip();
      if (sql[index] !== '(') return null;
      index += 1;
      const items: string[] = [];
      while (index < sql.length) {
        const item = literal();
        if (item == null) return null;
        items.push(item);
        skip();
        if (sql[index] === ',') {
          index += 1;
          continue;
        }
        if (sql[index] === ')') {
          index += 1;
          break;
        }
        return null;
      }
      if (items.length === 0) return null;
      return { column, operator: 'in', value: items.join(', ') };
    }
    if (word === 'like' || word === 'ilike') {
      consumeWord(word);
      const value = literal();
      if (value == null) return null;
      return { column, operator: word, value };
    }
    const operator = comparison();
    if (!operator || !COMPARISONS[operator]) return null;
    const value = literal();
    if (value == null) return null;
    return { column, operator: COMPARISONS[operator], value };
  };

  if (!consumeWord('select')) return null;
  skip();
  const fields: string[] = [];
  if (sql[index] === '*') {
    index += 1;
    fields.push('*');
  } else {
    while (index < sql.length) {
      const name = identifier();
      if (!name) return null;
      fields.push(name);
      skip();
      if (sql[index] === ',') {
        index += 1;
        continue;
      }
      break;
    }
  }
  if (fields.length === 0 || !consumeWord('from')) return null;
  const table = identifier();
  if (!table) return null;

  const conditions: ParsedCondition[] = [];
  if (peekWord()?.toLowerCase() === 'where') {
    consumeWord('where');
    while (index < sql.length) {
      const next = condition();
      if (!next) return null;
      conditions.push(next);
      if (peekWord()?.toLowerCase() === 'and') {
        consumeWord('and');
        continue;
      }
      break;
    }
  }

  const sorts: ParsedSort[] = [];
  if (peekWord()?.toLowerCase() === 'order') {
    consumeWord('order');
    if (!consumeWord('by')) return null;
    while (index < sql.length) {
      const column = identifier();
      if (!column) return null;
      let ascending = true;
      const direction = peekWord()?.toLowerCase();
      if (direction === 'asc' || direction === 'desc') {
        consumeWord(direction);
        ascending = direction === 'asc';
      }
      sorts.push({ column, ascending });
      skip();
      if (sql[index] === ',') {
        index += 1;
        continue;
      }
      break;
    }
  }

  let limit: number | undefined;
  if (peekWord()?.toLowerCase() === 'limit') {
    consumeWord('limit');
    skip();
    const digits = /^(\d+)/.exec(sql.slice(index));
    if (!digits || digits[1] === '0') return null;
    index += digits[1].length;
    limit = parseInt(digits[1], 10);
  }

  skip();
  if (index !== sql.length) return null;
  return { table, fields, conditions, sorts, limit };
}

export function parsedQuerySignature(query: ParsedBuilderQuery): string {
  return JSON.stringify({
    table: query.table,
    fields: query.fields,
    conditions: query.conditions.map((condition) => [condition.column, condition.operator, condition.value]),
    sorts: query.sorts.map((sort) => [sort.column, sort.ascending]),
    limit: query.limit ?? null,
  });
}
